/**
 * The shape builder (spec §7): turns any JSON value into view blocks by its
 * shape, then its format. Nothing is dropped — anything unclassifiable
 * becomes a JsonBlock.
 */
import { isPlainObject } from "../normalizeExtract";
import { humanize, legacyHumanize } from "../format/labels";
import {
  REVIEW_KEYS,
  addressDisplay,
  addressMissing,
  addressZip,
  detectFormat,
  formatScalar,
  isAddressLike,
  isEmptyValue,
  isSensitiveKey,
  maskValue,
} from "../format/registry";
import { agoText } from "../format/dates";
import { expiredNote } from "../checks/builtins";
import { parseFindings } from "../findings/parseFindings";
import { buildTable, isScalarArray, isTableArray } from "./buildTable";

const MAX_DEPTH = 4;

const firstSentenceCount = (s) => (String(s).match(/[.!?](\s|$)/g) || []).length;

export const isProse = (v) =>
  typeof v === "string" &&
  (v.length >= 160 || (v.length >= 120 && firstSentenceCount(v) >= 2));

const summarize = (obj) =>
  Object.entries(obj)
    .filter(([, v]) => !isEmptyValue(v) && !isPlainObject(v) && !Array.isArray(v))
    .slice(0, 2)
    .map(([k, v]) => formatScalar(k, v, { siblings: obj }).display)
    .join(" · ");

/**
 * Build a KV row for a scalar (or empty) value.
 * @param {object} opts { label, format, sensitive, note, parentKey, siblings, findable }
 */
export const kvRow = (key, value, path, ctx, opts = {}) => {
  const label = opts.label || humanize(key, { parentKey: opts.parentKey, booleanLike: typeof value === "boolean" });
  const empty = isEmptyValue(value);
  const base = {
    kind: "kv",
    path,
    key,
    label,
    value,
    display: null,
    format: opts.format || "text",
    empty,
    editable: false,
  };
  ctx.register?.(path, label, key);
  if (empty) return base;

  const f = formatScalar(key, value, { format: opts.format, siblings: opts.siblings });
  const row = {
    ...base,
    display: f.display,
    format: f.format,
    copyValue: f.copyValue,
    tone: f.tone,
  };
  if (opts.sensitive ?? isSensitiveKey(key, value)) {
    row.sensitive = { masked: maskValue(value) };
  }
  if (opts.note) row.note = opts.note;
  else if (!ctx.disabled?.has("expired")) {
    const exp = expiredNote(key, value, ctx.now);
    if (exp) row.note = { text: `Expired ${agoText(exp.months)}`, tone: "crit" };
  }
  const raw = typeof value === "string" ? value.trim() : typeof value === "number" ? String(value) : "";
  if (
    opts.findable !== false &&
    f.format !== "boolean" &&
    raw.length >= 3 &&
    !row.sensitive
  ) {
    row.findQuery = raw.length > 80 ? raw.slice(0, 80) : raw;
  }
  return row;
};

/** Address object → one KV row with parts (spec §8.6). */
export const addressRow = (key, value, path, ctx, opts = {}) => {
  const label = opts.label || humanize(key, { parentKey: opts.parentKey });
  ctx.register?.(path, label, key);
  const partKeys = [
    "fullAddress", "streetNumber", "streetName", "street", "line1", "address1",
    "addressUnitNumber", "line2", "city", "county", "state", "zip", "zipCode",
    "postalCode", "country", "apn",
  ].filter((k) => value[k] !== undefined && k !== "fullAddress");
  if (!partKeys.includes("state")) partKeys.push("state");
  if (!partKeys.some((k) => ["zip", "zipCode", "postalCode"].includes(k))) partKeys.push("zip");
  const parts = partKeys.map((k) => {
    const v = k === "zip" ? addressZip(value) : value[k];
    return {
      label: humanize(k),
      path: `${path}.${k}`,
      display: isEmptyValue(v) ? null : String(v),
      missing: isEmptyValue(v) && ["state", "zip", "zipCode", "postalCode"].includes(k),
    };
  });
  const display = addressDisplay(value);
  const missing = addressMissing(value);
  const row = {
    kind: "kv",
    path,
    key,
    label,
    value,
    display,
    format: "address",
    empty: !display,
    parts,
    copyValue: display || "",
    findQuery: value.city || value.streetName || undefined,
    editable: false,
  };
  if (opts.note !== undefined) {
    if (opts.note) row.note = opts.note;
  } else if (missing && !ctx.disabled?.has("address-incomplete")) {
    row.note = { text: `${missing.charAt(0).toUpperCase()}${missing.slice(1)} not provided`, tone: "warn" };
  }
  return row;
};

/**
 * @returns {object[]} blocks
 */
export function buildNode(key, value, path, depth, ctx, opts = {}) {
  if (ctx.consumed?.has(path) || ctx.hidden?.has(path)) return [];
  const label = opts.label || humanize(key, { parentKey: opts.parentKey, booleanLike: typeof value === "boolean" });

  if (isEmptyValue(value)) {
    if (REVIEW_KEYS.has(key) || opts.hideWhenEmpty) return [];
    return [kvRow(key, value, path, ctx, { ...opts, label })];
  }

  if (typeof value === "string") {
    if (/^CRITICAL/i.test(value.trim())) {
      ctx.register?.(path, label, key);
      return [{ kind: "prose", path, key, label, text: value.trim(), clampLines: 4, tone: "crit" }];
    }
    if (isProse(value)) {
      ctx.register?.(path, label, key);
      const parsed = parseFindings(value);
      if (parsed && parsed.findings.length >= 2) {
        return [{ kind: "findings", path, label, ...parsed }];
      }
      return [{ kind: "prose", path, key, label, text: value, clampLines: 4 }];
    }
  }

  if (typeof value !== "object") {
    return [kvRow(key, value, path, ctx, { ...opts, label })];
  }

  if (isPlainObject(value)) {
    if (isAddressLike(value)) return [addressRow(key, value, path, ctx, { ...opts, label })];
    if ("amount" in value && Object.keys(value).length <= 3 && detectFormat(key, value) === "money") {
      return [kvRow(key, value, path, ctx, { ...opts, label, format: "money" })];
    }
    ctx.register?.(path, label, key);
    if (depth > MAX_DEPTH) {
      return [{ kind: "json", path, label, json: JSON.stringify(value, null, 2) }];
    }
    const blocks = Object.entries(value).flatMap(([k, v]) =>
      buildNode(k, v, `${path}.${k}`, depth + 1, ctx, { parentKey: key, siblings: value }),
    );
    if (!blocks.length) return [];
    return [
      {
        kind: "object",
        path,
        label,
        depth,
        blocks,
        collapsedByDefault: depth >= 2,
        summary: depth >= 2 ? summarize(value) : undefined,
        fieldCount: blocks.length,
      },
    ];
  }

  if (isScalarArray(value)) {
    ctx.register?.(path, label, key);
    return [
      {
        kind: "list",
        path,
        label,
        presentation: value.every((x) => String(x ?? "").length <= 40) ? "chips" : "list",
        items: value
          .filter((x) => !isEmptyValue(x))
          .map((x, i) => ({
            display: formatScalar(key, x).display,
            path: `${path}[${i}]`,
            tone: opts.itemTone,
          })),
      },
    ];
  }

  if (isTableArray(value)) {
    ctx.register?.(path, label, key);
    if (opts.inTable) {
      return [{ kind: "json", path, label, json: JSON.stringify(value, null, 2) }];
    }
    const table = buildTable(key, value, path, { title: label, ...(opts.tableSpec || {}) }, {
      payload: ctx.payload,
      buildDetail: (k, v, p, parentKey, row) =>
        buildNode(k, v, p, MAX_DEPTH, ctx, { parentKey, siblings: row, inTable: true }),
    });
    table.rows.forEach((r) => ctx.register?.(r.path, r.title, r.rawTitle || ""));
    return [table];
  }

  ctx.register?.(path, label, key);
  return [{ kind: "json", path, label, json: JSON.stringify(value, null, 2) }];
}

/** The label the old FieldsPane gave a path (for focusField, spec §14). */
export const legacyLabelFor = (segments) => {
  // segments: [{ key } | { index }] relative to the file root; the old pane
  // dropped the top-level group key for objects but kept it for arrays.
  let label = "";
  segments.forEach((s, i) => {
    if (s.index !== undefined) {
      label = `${label} ${s.index + 1}`;
      return;
    }
    if (i === 0 && segments.length > 1 && segments[1].index === undefined) return;
    const h = legacyHumanize(s.key);
    label = label ? `${label} · ${h}` : h;
  });
  return label;
};
